import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Check, ChevronRight, SkipForward, UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";

const today = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD local
const localNow = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

export function PhotoCaptureDialog({ open, onClose, siteId, areaSuggestions = [], onUploaded }) {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setItems([]);
      setCurrent(0);
      // Open the native picker right away
      setTimeout(() => fileRef.current?.click(), 50);
    }
  }, [open]);

  useEffect(() => {
    return () => items.forEach((it) => it.url && URL.revokeObjectURL(it.url));
  }, [items]);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      onClose();
      return;
    }
    const next = files.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
      area_tag: "",
      attach_to_diary: false,
      client_visible: false,
      visited: false,
    }));
    setItems(next);
    setCurrent(0);
    e.target.value = "";
  };

  const patch = (idx, fields) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...fields, visited: true } : it)));

  const visitAndAdvance = () => {
    setItems((prev) => {
      const copy = [...prev];
      // smart default: carry area to next un-set photo
      copy[current] = { ...copy[current], visited: true };
      if (current + 1 < copy.length && !copy[current + 1].area_tag) {
        copy[current + 1] = { ...copy[current + 1], area_tag: copy[current].area_tag };
      }
      return copy;
    });
    if (current + 1 < items.length) setCurrent(current + 1);
  };

  const allVisited = items.length > 0 && items.every((it) => it.visited);
  const taggedCount = items.filter((it) => it.visited).length;

  const uploadAll = async () => {
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      const tags = [];
      const dates = [];
      const ats = [];
      items.forEach((it) => {
        fd.append("files", it.file, it.file.name);
        tags.push({
          area_tag: it.area_tag || null,
          attach_to_diary: it.attach_to_diary,
          client_visible: it.client_visible,
        });
        dates.push(today());
        ats.push(localNow());
      });
      fd.append("tags", JSON.stringify(tags));
      fd.append("taken_dates", JSON.stringify(dates));
      fd.append("taken_ats", JSON.stringify(ats));
      const res = await fetch(`${API}/sites/${siteId}/photos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Upload failed");
      }
      toast.success(`${items.length} photo${items.length > 1 ? "s" : ""} uploaded`);
      onUploaded?.();
      onClose();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const it = items[current];

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFiles}
        data-testid="photo-file-input"
      />
      <Dialog open={open && items.length > 0} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="photo-capture-dialog">
          <DialogHeader>
            <DialogTitle>
              Tag photos
              <span className="ml-2 text-sm font-normal text-slate-500">
                {taggedCount}/{items.length} tagged
              </span>
            </DialogTitle>
            <DialogDescription>
              Tag each photo with an area, diary link or client visibility, then upload.
            </DialogDescription>
          </DialogHeader>

          {it && (
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden bg-slate-100 aspect-[4/3] flex items-center justify-center">
                <img src={it.url} alt="" className="w-full h-full object-contain" />
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Area (optional)</Label>
                  <Input
                    list="area-suggestions"
                    value={it.area_tag}
                    onChange={(e) => patch(current, { area_tag: e.target.value })}
                    placeholder="e.g. Lot 12, Ground floor"
                    data-testid="capture-area-input"
                  />
                  <datalist id="area-suggestions">
                    {areaSuggestions.map((a) => (
                      <option key={a} value={a} />
                    ))}
                  </datalist>
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={it.attach_to_diary}
                    onCheckedChange={(v) => patch(current, { attach_to_diary: !!v })}
                    data-testid="capture-diary-checkbox"
                  />
                  Attach to today's diary entry
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={it.client_visible}
                    onCheckedChange={(v) => patch(current, { client_visible: !!v })}
                    data-testid="capture-client-checkbox"
                  />
                  Visible to client
                </label>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={visitAndAdvance}
                  disabled={current >= items.length - 1 && it.visited}
                  data-testid="capture-skip-btn"
                >
                  <SkipForward className="w-4 h-4 mr-2" /> Skip
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  onClick={visitAndAdvance}
                  data-testid="capture-next-btn"
                >
                  {current < items.length - 1 ? (
                    <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
                  ) : (
                    <>Mark tagged <Check className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
              </div>

              {/* Filmstrip */}
              <div className="flex gap-2 overflow-x-auto pb-1 border-t pt-3">
                {items.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrent(i)}
                    className={`relative shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${
                      i === current ? "border-amber-500" : "border-transparent"
                    }`}
                    data-testid={`filmstrip-thumb-${i}`}
                  >
                    <img src={p.url} alt="" className="w-full h-full object-cover" />
                    {p.visited && (
                      <span className="absolute top-0.5 right-0.5 bg-green-600 rounded-full p-0.5">
                        <Check className="w-3 h-3 text-white" />
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <Button
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                disabled={!allVisited || uploading}
                onClick={uploadAll}
                data-testid="upload-all-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
                ) : (
                  <><UploadCloud className="w-4 h-4 mr-2" /> Upload all ({items.length})</>
                )}
              </Button>
              {!allVisited && (
                <p className="text-xs text-slate-400 text-center">
                  Visit every photo (tag or skip) to enable upload.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
